<template>
  <div class="max-w-[664px]">
    <div v-if="isLoading" class="py-4 text-sm text-gray-500">
      Loading passkeys...
    </div>

    <div v-else>
      <div v-if="passkeys.length > 0" class="mb-4">
        <div
          v-for="passkey in passkeys"
          :key="passkey.id"
          class="flex items-center justify-between border-b border-gray-200 py-3"
        >
          <div>
            <p class="text-sm font-medium text-gray-900">
              {{ passkey.name || 'Passkey' }}
            </p>
            <p class="text-xs text-gray-500">
              Added {{ formatTime(passkey.created_at) }}
            </p>
          </div>
          <VButton
            variant="outline-secondary"
            class="text-xs"
            :isLoading="removingId === passkey.id"
            loadingText="Removing..."
            @click="handleRemove(passkey)"
          >
            Remove
          </VButton>
        </div>
      </div>

      <p v-else class="mb-4 text-sm text-gray-500">
        No passkeys registered yet. Add one to enable passwordless sign-in.
      </p>

      <p v-if="error" class="mb-3 text-sm text-red-600">{{ error }}</p>

      <VButton
        variant="outline-secondary"
        :isLoading="isRegistering"
        loadingText="Registering..."
        @click="handleAdd"
      >
        Add passkey
      </VButton>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import VButton from '@/components/ui/VButton.vue';
import { formatTime } from '@/filters';
import { usePasskeys } from '@/composables/usePasskeys';

const toast = useToast();
const {
  passkeys,
  isLoading,
  error,
  fetchPasskeys,
  registerPasskey,
  removePasskey,
} = usePasskeys();

const isRegistering = ref(false);
const removingId = ref(null);

async function handleAdd() {
  isRegistering.value = true;
  error.value = null;
  try {
    const name = window.prompt('Enter a name for this passkey (optional):');
    if (name === null) {
      isRegistering.value = false;
      return;
    }
    await registerPasskey(name || 'My passkey');
    toast.success('Passkey registered successfully.');
    await fetchPasskeys();
  } catch (err) {
    if (err.name === 'NotAllowedError') {
      error.value = 'Passkey registration was cancelled.';
    } else {
      error.value =
        err.response?.data?.message ||
        'Failed to register passkey. Please try again.';
    }
  } finally {
    isRegistering.value = false;
  }
}

async function handleRemove(passkey) {
  const confirmed = window.confirm(
    `Remove passkey "${passkey.name || 'Passkey'}"? This cannot be undone.`,
  );
  if (!confirmed) return;

  removingId.value = passkey.id;
  error.value = null;
  try {
    await removePasskey(passkey.id);
    toast.success('Passkey removed.');
  } catch (err) {
    error.value = err.response?.data?.message || 'Failed to remove passkey.';
  } finally {
    removingId.value = null;
  }
}

onMounted(fetchPasskeys);
</script>
